/**
 * Campus Picker Widget - Embeddable Bundle Entry Point
 *
 * Two forms, chosen by `data-mode` on the container (or `mode` in
 * window.CAMPUS_PICKER_CONFIG):
 *
 *   dropdown (default)  header trigger that opens the panel as a menu under
 *                       the header (a full-height sheet on phones)
 *     <div id="campus-picker-root"></div>
 *
 *   panel               every campus as cards, for the Locations mega menu
 *     <div id="campus-picker-root" data-mode="panel"></div>
 *
 *   <script src="https://widgets.woodsidebible.org/campus-picker/widget.js" defer></script>
 *
 * Optional attributes: data-details-url (campus pages live under it, default
 * https://woodsidebible.org/locations), data-view-all-url, data-api-base-url,
 * data-image-url (the photo behind the heading; set it empty for no photo).
 *
 * Header form without a container: hang the panel off the theme's own nav
 * link and let its label show the pick. From the site-wide head box:
 *   <script>window.CAMPUS_PICKER_CONFIG = {
 *     triggerSelector: '.menu-item-151 > a',
 *     labelSelector: '.menu-item-151 > a .menu-title-text' };</script>
 *   <script src="https://widgets.woodsidebible.org/campus-picker/widget.js" defer></script>
 *
 * Phones: Salient's fullscreen menu ignores the mega menu, so in panel mode
 * every other copy of the nav item (the off-canvas menu's "Locations" link)
 * opens the same panel as a bottom sheet instead of navigating; "View all
 * locations" is on the sheet's tile. No configuration needed.
 *
 * Shadow DOM isolation following the locations widget pattern. The header
 * form's open panel portals into a SECOND shadow root on document.body (see
 * the group-finder shell for why): on woodsidebible.org the header sits in
 * stacking contexts under the theme's fixed header, so a fixed overlay
 * rendered inside the widget's own root would be painted over.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { CampusPicker } from '../src/components/CampusPicker';
import { CampusPanel, DEFAULT_DETAILS_BASE_URL } from '../src/components/CampusPanel';
import styleText from '../src/app/globals.css?inline';
import { getSelectedCampus, onCampusChange, onLiveStatus } from '../src/lib/campus-storage';
import { findNavItemTargets, syncNavItem, syncNavLive } from '../src/lib/navItem';
import { scrollToSectionFromHash } from '../src/lib/scrollToSection';

type Mode = 'dropdown' | 'panel';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  mode?: Mode;
  detailsBaseUrl?: string;
  viewAllUrl?: string;
  /** Photo behind the panel heading; empty string = no photo tile */
  imageUrl?: string;
  /**
   * Header form only: open from an element the page already has (the theme's
   * "Locations" nav link, e.g. '.menu-item-151 > a') instead of rendering a
   * trigger. With this set the container is optional; one is created.
   */
  triggerSelector?: string;
  /**
   * The header element whose text becomes the pick's short name ("TR"). By
   * default the nav item the widget renders inside (a Salient mega menu),
   * every copy of it; set to '' to leave the header alone.
   */
  labelSelector?: string;
  /** The header icon the campus art replaces; same default as labelSelector */
  iconSelector?: string;
}

declare global {
  interface Window {
    CAMPUS_PICKER_CONFIG?: WidgetConfig;
    CampusPickerWidget?: CampusPickerWidgetInstance;
  }
}

function attr(value: string | undefined | null): string | undefined {
  const v = value?.trim();
  return v ? v : undefined;
}

class CampusPickerWidgetInstance {
  private root: ReturnType<typeof createRoot> | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: WidgetConfig;
  private static readonly OVERLAY_HOST_ID = 'campus-picker-overlay';
  // Above the theme's fixed header (#header-outer is z-index 9999 on woodsidebible.org)
  private static readonly OVERLAY_Z_INDEX = '2147483000';

  constructor(config: WidgetConfig = {}) {
    this.config = {
      containerId: 'campus-picker-root',
      ...config,
    };
  }

  init() {
    let container = document.getElementById(this.config.containerId!);
    if (!container && this.config.triggerSelector) {
      // No trigger of our own to show, so the root only needs to exist
      container = document.createElement('div');
      container.id = this.config.containerId!;
      container.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
      document.body.appendChild(container);
    }
    if (!container) {
      console.error(`CampusPicker: Container #${this.config.containerId} not found`);
      return;
    }

    // The container's data-* attributes win over the config object so one
    // script tag can serve a dropdown in the header and a panel in the menu.
    const mode: Mode =
      (attr(container.dataset.mode) as Mode | undefined) ?? this.config.mode ?? 'dropdown';
    const apiBaseUrl = attr(container.dataset.apiBaseUrl) ?? this.config.apiBaseUrl;
    const detailsBaseUrl = attr(container.dataset.detailsUrl) ?? this.config.detailsBaseUrl;
    const viewAllUrl = attr(container.dataset.viewAllUrl) ?? this.config.viewAllUrl;
    const imageUrl = container.dataset.imageUrl ?? this.config.imageUrl;
    const triggerSelector = attr(container.dataset.trigger) ?? this.config.triggerSelector;
    const labelSelector = container.dataset.label ?? this.config.labelSelector;
    const iconSelector = container.dataset.icon ?? this.config.iconSelector;

    // Re-use existing shadow root if already initialized
    if (container.shadowRoot) {
      this.shadowRoot = container.shadowRoot;
      const existingApp = this.shadowRoot.getElementById('campus-picker-app');
      if (existingApp) {
        console.log('Campus Picker Widget: already initialized, skipping');
        return;
      }
    } else {
      this.shadowRoot = container.attachShadow({ mode: 'open' });
    }

    const mountPoint = document.createElement('div');
    mountPoint.id = 'campus-picker-app';
    this.shadowRoot.appendChild(mountPoint);

    this.injectFont();
    this.injectStyles(this.shadowRoot, mode);

    const portalContainer = mode === 'panel' ? null : this.mountOverlayHost();

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        {mode === 'panel' ? (
          <CampusPanel
            apiBaseUrl={apiBaseUrl}
            detailsBaseUrl={detailsBaseUrl}
            viewAllUrl={viewAllUrl}
            imageUrl={imageUrl}
            onNavigate={() => closeMegaMenu(container)}
          />
        ) : (
          <CampusPicker
            apiBaseUrl={apiBaseUrl}
            detailsBaseUrl={detailsBaseUrl}
            viewAllUrl={viewAllUrl}
            imageUrl={imageUrl}
            portalContainer={portalContainer}
            triggerSelector={triggerSelector}
          />
        )}
      </React.StrictMode>
    );

    // The panel applies this default itself; the nav item needs it spelled out
    this.syncNavItem(
      container,
      detailsBaseUrl ?? DEFAULT_DETAILS_BASE_URL,
      labelSelector,
      iconSelector
    );
    if (mode === 'panel') {
      this.mountPhoneSheet(container, { apiBaseUrl, detailsBaseUrl, viewAllUrl, imageUrl });
    }
  }

  /**
   * Panel mode on the site: the desktop mega menu holds the panel, but the
   * phone menu only has the plain "Locations" link. Tag every copy of the nav
   * link that is NOT the one wrapping this panel and hang the header form's
   * sheet off it, so a tap opens the campuses instead of leaving the page.
   * Salient's off-canvas menu is closed first so the sheet is not behind it.
   */
  private mountPhoneSheet(
    container: HTMLElement,
    props: {
      apiBaseUrl?: string;
      detailsBaseUrl?: string;
      viewAllUrl?: string;
      imageUrl?: string;
    }
  ) {
    const { links } = findNavItemTargets(container);
    const others = links.filter((a) => !a.closest('li')?.contains(container));
    if (others.length === 0) return;
    const ATTR = 'data-campus-picker-sheet';
    for (const a of others) a.setAttribute(ATTR, '');

    // Runs before the sheet's own (document-level) click handler
    window.addEventListener(
      'click',
      (e) => {
        const hit = (e.target as Element | null)?.closest?.(`[${ATTR}]`);
        if (!hit) return;
        const toggle = document.querySelector<HTMLElement>(
          '.slide-out-widget-area-toggle a.open, .slide-out-widget-area-toggle a[aria-expanded="true"]'
        );
        if (toggle) setTimeout(() => toggle.click(), 0);
      },
      true
    );

    const portal = this.mountOverlayHost();
    const sheetMount = document.createElement('div');
    portal.parentNode?.appendChild(sheetMount);
    createRoot(sheetMount).render(
      <React.StrictMode>
        <CampusPicker {...props} portalContainer={portal} triggerSelector={`[${ATTR}]`} />
      </React.StrictMode>
    );
  }

  /**
   * The header nav item shows the pick: campus art for its icon, short name
   * for its label. Runs now and on every change, for the item the widget sits
   * in unless selectors say otherwise ('' opts out).
   */
  private syncNavItem(
    container: HTMLElement,
    detailsBaseUrl: string | undefined,
    labelSelector?: string,
    iconSelector?: string
  ) {
    if (labelSelector === '' && iconSelector === '') return;
    const targets = findNavItemTargets(
      container,
      labelSelector || undefined,
      iconSelector || undefined
    );
    if (targets.labels.length === 0 && targets.icons.length === 0) return;
    syncNavItem(targets, getSelectedCampus(), detailsBaseUrl);
    // The live dot on the art: the panel's hook polls Sardius and broadcasts
    // the streaming campuses; the dot follows the pick and the latest poll.
    let liveIds = new Set<number>();
    let selectedId = getSelectedCampus()?.congregationId ?? null;
    const relight = () => syncNavLive(targets, selectedId != null && liveIds.has(selectedId));
    onLiveStatus((ids) => {
      liveIds = ids;
      relight();
    });
    onCampusChange((campus) => {
      syncNavItem(targets, campus, detailsBaseUrl);
      selectedId = campus.congregationId;
      relight();
    });
  }

  /**
   * A zero-size host at the end of <body> with its own shadow root and a
   * top-level z-index, so the fixed panel inside stacks against the page
   * rather than inside the header's stacking context. It gets the same
   * stylesheet so the panel looks the same as the inline form.
   */
  private mountOverlayHost(): HTMLElement {
    const id = CampusPickerWidgetInstance.OVERLAY_HOST_ID;
    let host = document.getElementById(id);
    if (!host) {
      host = document.createElement('div');
      host.id = id;
      host.style.cssText = `position:relative;z-index:${CampusPickerWidgetInstance.OVERLAY_Z_INDEX};width:0;height:0;`;
      document.body.appendChild(host);
    }
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    root.replaceChildren();
    const container = document.createElement('div');
    container.id = 'campus-picker-app';
    root.appendChild(container);
    this.injectStyles(root, 'panel');
    return container;
  }

  private injectFont() {
    const fontId = 'campus-picker-montserrat';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Bebas+Neue&display=swap';
      document.head.appendChild(link);
    }
  }

  private injectStyles(root: ShadowRoot, mode: Mode) {
    const resetCSS = `
      *, *::before, *::after { box-sizing: border-box; }

      :host {
        display: ${mode === 'panel' ? 'block; width: 100%' : 'inline-block'};
        font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif;

        --tw-border-spacing-x: 0;
        --tw-border-spacing-y: 0;
        --tw-translate-x: 0;
        --tw-translate-y: 0;
        --tw-rotate: 0;
        --tw-skew-x: 0;
        --tw-skew-y: 0;
        --tw-scale-x: 1;
        --tw-scale-y: 1;
        --tw-pan-x: ;
        --tw-pan-y: ;
        --tw-pinch-zoom: ;
        --tw-scroll-snap-strictness: proximity;
        --tw-gradient-from-position: ;
        --tw-gradient-via-position: ;
        --tw-gradient-to-position: ;
        --tw-ordinal: ;
        --tw-slashed-zero: ;
        --tw-numeric-figure: ;
        --tw-numeric-spacing: ;
        --tw-numeric-fraction: ;
        --tw-ring-inset: ;
        --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff;
        --tw-ring-color: rgb(59 130 246 / 0.5);
        --tw-ring-offset-shadow: 0 0 #0000;
        --tw-ring-shadow: 0 0 #0000;
        --tw-shadow: 0 0 #0000;
        --tw-shadow-colored: 0 0 #0000;
        --tw-inset-shadow: 0 0 #0000;
        --tw-inset-ring-shadow: 0 0 #0000;
        --tw-border-style: solid;
        --tw-blur: ;
        --tw-brightness: ;
        --tw-contrast: ;
        --tw-grayscale: ;
        --tw-hue-rotate: ;
        --tw-invert: ;
        --tw-saturate: ;
        --tw-sepia: ;
        --tw-drop-shadow: ;
        --tw-backdrop-blur: ;
        --tw-backdrop-brightness: ;
        --tw-backdrop-contrast: ;
        --tw-backdrop-grayscale: ;
        --tw-backdrop-hue-rotate: ;
        --tw-backdrop-invert: ;
        --tw-backdrop-opacity: ;
        --tw-backdrop-saturate: ;
        --tw-backdrop-sepia: ;
        --tw-contain-size: ;
        --tw-contain-layout: ;
        --tw-contain-paint: ;
        --tw-contain-style: ;
      }

      #campus-picker-app {
        font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif;
      }
    `;

    // Replace :root → :host and body → #campus-picker-app for shadow DOM
    const shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, '#campus-picker-app');

    const style = document.createElement('style');
    style.textContent = resetCSS + '\n' + shadowCSS;
    root.appendChild(style);
  }

  destroy() {
    this.root?.unmount();
    this.root = null;
  }
}

const LEAVING_CLASS = 'campus-picker-leaving';

/**
 * A click on a link in the mega menu panel leaves the page, but the menu is
 * open on CSS :hover and the pointer is still over it, so it would stay open
 * (page dimmed behind it) until the next page paints. Hide the menu and the
 * theme's body::before dimmer now. They come back the next time the
 * Locations item is hovered, or when the page is restored from the back/
 * forward cache.
 */
function closeMegaMenu(container: HTMLElement) {
  const item = container.closest('li');
  const menu = (container.closest('.nectar-global-section-megamenu') ??
    container.closest('ul.sub-menu')) as HTMLElement | null;
  if (!item || !menu) return;

  if (!document.getElementById(LEAVING_CLASS)) {
    const style = document.createElement('style');
    style.id = LEAVING_CLASS;
    style.textContent = `html.${LEAVING_CLASS} body::before{opacity:0!important;visibility:hidden!important;transition:opacity .15s ease,visibility 0s .15s!important}`;
    document.head.appendChild(style);
  }
  document.documentElement.classList.add(LEAVING_CLASS);
  // Inline !important outranks the theme's own !important rules
  menu.style.setProperty('opacity', '0', 'important');
  menu.style.setProperty('visibility', 'hidden', 'important');
  menu.style.setProperty('transition', 'opacity .15s ease, visibility 0s .15s', 'important');
  item.classList.remove('sfHover');

  const trigger = item.querySelector(':scope > a');
  const reset = () => {
    document.documentElement.classList.remove(LEAVING_CLASS);
    menu.style.removeProperty('opacity');
    menu.style.removeProperty('visibility');
    menu.style.removeProperty('transition');
    trigger?.removeEventListener('mouseenter', reset);
    window.removeEventListener('pageshow', reset);
  };
  trigger?.addEventListener('mouseenter', reset);
  window.addEventListener('pageshow', reset);
}

if (typeof window !== 'undefined') {
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    containerId: 'campus-picker-root',
    ...(window.CAMPUS_PICKER_CONFIG || {}),
  };

  if (!window.CampusPickerWidget) {
    const boot = () => {
      const widget = new CampusPickerWidgetInstance(config);
      widget.init();
      // A link from the panel to a section of this page (#announcements)
      scrollToSectionFromHash();
      window.CampusPickerWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default CampusPickerWidgetInstance;
